import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, firstValueFrom, Subject } from 'rxjs';
import { CredentialService } from 'src/app/services/core/credential/credential.service';
import { UploadService } from 'src/app/services/core/documents/upload.service';
import { FundingSourceService } from 'src/app/services/core/funding-source/funding-source.service';
import { MemberService } from 'src/app/services/core/member/member.service';
import { RoleService } from 'src/app/services/core/role/role.service';
import { UserService } from 'src/app/services/core/users/user.service';
import { imageProcessing, ImageSnippet } from 'src/app/utils/image-processing-utils';
import { sweetAlert } from 'src/app/utils/swal';
import * as moment from 'moment-timezone';
import { timeZone } from 'src/app/utils/utils.common';
import { urls } from 'src/app/utils/url.utils';
import { CamelCasePipe } from 'src/app/pipes/camelcase.filter/camelcase.filter.pipe';
import { TitleCasePipe } from '@angular/common';
import { validateFileSize } from 'src/app/utils/common.utils';
@UntilDestroy()

@Component({
  selector: 'app-add-user',
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.css']
})
export class AddUserComponent implements OnInit {
  userForm!: FormGroup;
  showPassword = false;
  submitted = false;
  userId: any;
  companies: any;
  profileImageURL: string | null = null;
  selectedFile: ImageSnippet | null = null;
  roles:any = [];
  types: any = [ ];
  selectedRoleType:any
  fundingSources:any = [];
  members:any = [];
  controlName: string = '';
  timezones:any = moment.tz.names();
  private readonly memberSearchSubject = new Subject<string | undefined>();
  memberSearchQuery: any;
  memberPageNumber = 1;
  memberSearchPageNumber = 1;
  memberList:any = [];
  preMembers:any = {}

  @Input() user:any;
  @Input() source:any;
  @Output() onSaveUser = new EventEmitter<string>();
  @Output() onCloseModal = new EventEmitter<string>();

  
  constructor(
    private uploadService: UploadService,
    private credentialService: CredentialService,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private roleService: RoleService,
    private fundingSourceService: FundingSourceService,
    private memberService: MemberService,
  ) { }

  ngOnInit(): void {

    this.getCompanies();
    this.userForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      role: [null, Validators.required],
      isActive: [true, Validators.required],
      profileImageURL: ['', Validators.required],
      company: [null],
      fundingsource:  [null],
      member:  [null],
      timeZone: [timeZone, Validators.required],
    });

     if (this.source === 'modal' && this.user) {
      this.userId = this.user.id;
    }
 
    this.getRoles();
    this.listenMemberSearch();
  }

  onInputChange(event: any) {
    if (this.userId) {
      const input = event.target.value;
      this.applyPasswordValidations(input);
    }
  }

  getRoles() {
    const queryParams = {
      limit: 9999,
      isActive: true
    }
    this.roleService.getPickerRoles(queryParams)
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        if (data && data.results) {
          // this.roles = data.results
          this.roles = data.results.map((item: any) => {
            return {
              ...item,
              name: new TitleCasePipe().transform(item.name)
            };
          });
        }
        if (this.userId) {
          this.getUserById();
        }
      })
  }
  

 async onRoleChange(selectedRoleId: any) {
    this.userForm.get('company')?.setValue(null);
    this.userForm.get('fundingsource')?.setValue(null);
    this.userForm.get('member')?.setValue(null);
    const selectedRole = this.roles.find((r: any) => r.id === selectedRoleId);
    this.selectedRoleType = selectedRole ? selectedRole.type : null;
    await this.patchRole();
  }

  async patchRole() {
    this.userForm.get('company')?.clearValidators();
    this.userForm.get('fundingsource')?.clearValidators();
    this.userForm.get('member')?.clearValidators();
    if (this.selectedRoleType === 'fundingsource') {
      this.types = await this.getFundingSources();
      this.types = this.transformToTitleCase(this.types, 'title');
      this.userForm.get('fundingsource')?.setValidators([Validators.required]);
    } else if (this.selectedRoleType === 'member') {
      this.types = await this.getAllMembers();
      this.userForm.get('member')?.setValidators([Validators.required]);
    } else if (this.selectedRoleType === 'company') {
      this.types = await this.getCredentialCompanies();
      this.types = this.transformToTitleCase(this.types, 'name');
      this.userForm.get('company')?.setValidators([Validators.required]);
    } else {
    // Clear values for all dependent fields
    this.userForm.get('company')?.setValue(null);
    this.userForm.get('fundingsource')?.setValue(null);
    this.userForm.get('member')?.setValue(null);
      this.types = [];
    }
    // Update validity
    this.userForm.get('company')?.updateValueAndValidity();
    this.userForm.get('fundingsource')?.updateValueAndValidity();
    this.userForm.get('member')?.updateValueAndValidity();
  }

  transformToTitleCase(list: any, key: any) {
    return list.map((item: any) => ({
      ...item,
      [key]: new TitleCasePipe().transform(item[key])
    }));
  }

  onSelectEntity(event: any) {
    this.userForm.patchValue({
      timeZone: event.timeZone
    })
  }

  async getFundingSources() {
    const queryParams = {
      limit: 9999,
      isActive: true
    }
    const response: any = await firstValueFrom(
      this.fundingSourceService.getPicker(queryParams).pipe(untilDestroyed(this))
    );
    return response?.results || [];
  }


  memberSearch(search: any, id = null) {
    const queryParams: any = {
      limit: 10,
      page: this.memberSearchPageNumber
    }

    if (search) {
      queryParams.search = search;
    }

    if (id) {
      queryParams.id = id;
    }

      queryParams.isActive = true;

    this.memberService.searchMembers(queryParams).pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data && data.results) {
        this.memberList = [...this.memberList, ...data.results];
        this.preMembers = this.memberList.reduce((obj: any, item: any) => {
          obj[item.id] = item;
          return obj;
        }, {});
        this.memberSearchPageNumber += 1;
      }
    });
  }

  getAllMembers() {
    const queryParams: any = {
      limit: 10,
      page: this.memberPageNumber,
    }

      queryParams.isActive = true;

    this.memberService.getPickerMembers(queryParams).pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data && data.results) {
        this.memberList = [...this.memberList, ...data.results];
        this.preMembers = this.memberList.reduce((obj: any, item: any) => {
          obj[item.id] = item;
          return obj;
        }, {});
        this.memberPageNumber += 1;
      }
    })
  }
  
  async getCredentialCompanies(){
    const queryParams = {
      limit: 9999,
      isActive: true,
      isApproved: true
    }
    // this.credentialService.getCompanies(queryparams)
    //   .pipe(untilDestroyed(this)).subscribe((data: any) => {
    //     if (data && data.results ) {
    //       return data
    //     }
    //   })
    const response: any = await firstValueFrom(
      this.credentialService.getPicker(queryParams).pipe(untilDestroyed(this))
    );
    return response?.results || [];
  }

  onSelectMember(event: any) {
    if (this.preMembers && event && this.preMembers[event])
    this.userForm.patchValue({
      timeZone: this.preMembers[event].timeZone
    })
  }
  


  onSave() {
    this.submitted = true;
   if (this.userForm.invalid) return;
  
    let payload = this.userForm.value;

    if (this.userId && !payload.password) {
      delete payload.password;
    }
    this.userService.saveUser(this.userId, payload).pipe(untilDestroyed(this)).subscribe(
      (data: any) => {
        if (data) {
          if (this.source === 'modal') {
            this.onSaveUser.emit(data);
          } else {
             this.router.navigateByUrl(`/users`);
          }
        }
       
      },
      (err) => {
        sweetAlert("Error", err, "error", "OK");
      }
    );
  }

  applyPasswordValidations(value: any) {
    const passwordControl = this.userForm.get('password');

    if (value) {
      passwordControl?.setValidators([Validators.required, Validators.minLength(8)]);
    } else {
      passwordControl?.clearValidators();
    }
    passwordControl?.updateValueAndValidity();
  }

  getUserById() {
    this.userService.getUserById(this.userId).pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data) {
        const { member, ...withoutMemberObj } = data;
        this.userForm.patchValue({
          ...withoutMemberObj,
          role: data.role ? data.role.id : ''
        })
        
        this.memberSearch(null, data.member);
        this.selectedRoleType = this.roles.find((r: any) => r.id === data.role.id)?.type;
        
        if(this.selectedRoleType == 'member'){
          this.getMembersById(data.member)
        }
        
        if(this.selectedRoleType == 'fundingsource'){
          this.getFundingSourceById(data.fundingsource);
        }

        this.profileImageURL = data.profileImageURL;
        // clear password validations
        this.userForm.get('password')?.clearValidators();
        this.userForm.get('password')?.updateValueAndValidity();
        if (this.selectedRoleType) {
           this.patchRole();
        }
       
      }
      
    })
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  getCompanies() {
    const queryparams = {
      limit: 999
    }
    this.credentialService.getPicker(queryparams)
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        if (data && data.results ) {
          this.companies = data.results;
        }
      })
  }

  processFile(imageInput: any) {
    const file = imageInput.files?.[0];
    if (!validateFileSize(file)) {
      return;
    }
    imageProcessing(imageInput).pipe(untilDestroyed(this)).subscribe({
      next: (result) => {
        this.selectedFile = result.file;
        if (!this.selectedFile || !(this.selectedFile instanceof Blob)) {
          return;
        }
        this.uploadService.uploadImageToServer(this.selectedFile, urls.USERS).pipe(untilDestroyed(this)).subscribe({
          next: (uploadResult: any) => {
            if (uploadResult?.fileUrl) {
              this.userForm.get('profileImageURL')?.setValue(uploadResult.fileUrl);
              this.profileImageURL = uploadResult.fileUrl;
            }
          },
        });
      },
      error: (processingError) => {
        console.error('Error processing image:', processingError);
      },
    });
  }

  get form() {
    return this.userForm.controls;
  }

  onClose() {
    if (!this.userForm.value.member && this.memberSearchQuery) {
      this.clearSearh();
    }
  }

  onClear() {
    if (this.memberSearchQuery) {
      this.clearSearh();
    }
  }

  clearSearh() {
    this.memberSearchQuery = '';
    this.memberSearchPageNumber = 1;
    this.memberList = [];
    this.memberPageNumber = 1
    this.getAllMembers();
  }

  onScroll() {
    if (this.memberSearchQuery) {
      this.memberSearch(this.memberSearchQuery);
    } else {
      this.getAllMembers();
    }
  }

  onSearch(event: any) {
    const { term } = event;
    if (term) {
      this.memberSearchSubject.next(term);
    } else {
      this.clearSearh();
    }
  }


    listenMemberSearch() {
      this.memberSearchSubject
        .pipe(debounceTime(1000))
        .pipe(untilDestroyed(this)).subscribe((search) => {
          this.memberSearchQuery = search;
          if (search && search.length >= 3) {
            this.memberList = [];
            this.memberSearchPageNumber = 1;
            this.memberSearch(search);
          }
        });
    }

  getMembersById(memberId:any) {
    this.memberService.getMemberById(memberId).pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data) {
          this.userForm.patchValue({
          member : data.isActive ? data.id : ''
        })
      }
    })
  } 

  getFundingSourceById(fsId:any) {
    this.fundingSourceService
      .getById(fsId)
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        if (data) {
          this.userForm.patchValue({
          fundingsource : data.isActive ? data.id : null
        })
        }
      })
  }
}
